import { useEffect, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { getEmailRule, updateEmailRule } from '@/services/emailRules'
import type { EmailRule, EmailRuleCreate } from '@/types/emailRule'
import EmailRuleForm from './EmailRuleForm'

interface EmailRuleEditModalProps {
  ruleId: number
  onClose: () => void
  onSaved?: (rule: EmailRule) => void
  /** Optional context shown below the title (e.g. "Used to process this document"). */
  subtitle?: string
}

export default function EmailRuleEditModal({
  ruleId,
  onClose,
  onSaved,
  subtitle,
}: EmailRuleEditModalProps) {
  const [rule, setRule] = useState<EmailRule | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    getEmailRule(ruleId)
      .then((data) => {
        if (!cancelled) setRule(data)
      })
      .catch((err) => {
        console.error(err)
        if (!cancelled) setError('Failed to load email rule')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [ruleId])

  async function handleSubmit(data: EmailRuleCreate) {
    setSaving(true)
    setError(null)
    try {
      const updated = await updateEmailRule(ruleId, data)
      setRule(updated)
      onSaved?.(updated)
      onClose()
    } catch (err) {
      console.error(err)
      setError('Failed to update email rule')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-2 sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose()
      }}
    >
      <div
        className="max-h-[95vh] w-full min-w-0 max-w-lg overflow-y-auto rounded-xl bg-white shadow-2xl sm:max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="email-rule-edit-title"
      >
        <div className="sticky top-0 z-10 flex items-start justify-between border-b border-gray-100 bg-white px-4 py-3 sm:px-6 sm:py-4">
          <div className="min-w-0 pr-2">
            <h3
              id="email-rule-edit-title"
              className="truncate text-lg font-semibold text-gray-900"
            >
              {rule?.vendor_name ? `Edit ${rule.vendor_name}` : 'Edit Email Rule'}
            </h3>
            {subtitle && (
              <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="btn btn-ghost p-1 flex-shrink-0"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6">
          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="py-12 text-center text-sm text-gray-500">Loading rule…</div>
          ) : rule ? (
            <EmailRuleForm
              key={rule.id}
              initialData={rule}
              onSubmit={handleSubmit}
              onCancel={onClose}
              isModal
            />
          ) : (
            <div className="py-12 text-center text-sm text-gray-500">
              Email rule not found.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
